// @flow

// Skipping story doc for this component since it is a base class component meant only to be extended by its subclasses
/* eslint-disable ta-code-conventions/has-matching-storydoc */

import * as React from 'react';
import Loadable from './Loadable';

/** The component props for a <Load> */
export type Props = {|
  /** A function to get a component, or a Loadable instance. */
  +component: Loadable | (() => any),

  /**
   * Render prop that gives the child the component class and whether
   * loading is either in progress or failed.
   */
  +children?: ({|
    +Component: ?React.ComponentType<any>,
    +errored: boolean,
    +loading: boolean,
  |}) => React.Node,
|};

type State = {|
  importer: Loadable | (() => any),
  loadable: Loadable,
|};

/**
 * A component that handles loading and error states while loading a child component.
 *
 * The caller provides either a function that imports a component, or a Loadable. If
 * you provide a function, this component will internally wrap it in a Loadable.
 */
class Load extends React.Component<Props, State> {
  static newState(props: Props): State {
    return {
      importer: props.component,
      loadable:
        props.component instanceof Loadable ? props.component : new Loadable(props.component),
    };
  }

  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    if (prevState.importer !== nextProps.component) {
      return Load.newState(nextProps);
    }
    return null;
  }

  constructor(props: Props) {
    super(props);
    this.state = Load.newState(props);
  }

  componentDidMount() {
    if (!this.state.loadable.isLoaded() && !this.state.loadable.isLoading()) {
      this.state.loadable.getPromise().finally(() => this.forceUpdate());
    }
  }

  // For this function code coverage was disabled since it was possible to test only one render case
  /* istanbul ignore next */
  render() {
    const { children } = this.props;

    if (!children) {
      // No render prop. Render the component if it is available.
      if (this.state.loadable.isLoaded() && this.state.loadable.get()) {
        return React.createElement(this.state.loadable.get());
      }
      return null;
    }

    // isLoaded and isErrored will both be true when an error occurs.
    // Did it fail?
    if (this.state.loadable.isErrored()) {
      return children({
        Component: null,
        errored: true,
        loading: false,
      });
    }

    // Do we have it?
    else if (this.state.loadable.isLoaded()) {
      return children({
        Component: this.state.loadable.get(),
        errored: false,
        loading: false,
      });
    }

    // It must be loading (or about to load)
    return children({
      Component: null,
      errored: false,
      loading: true,
    });
  }
}

export default Load;
